define((require) => {
    "use strict";

    const _ = require("underscore");
    const EventDispatcher = require("shared/util/EventDispatcher");
    const Marionette = require("marionette");

    /**
     * Represents a view scope, shared by the parent view and all it's children.
     *
     * By subclassing <tt>ImprovedView</tt>, you'll be able to declare a <tt>viewScope</tt>
     * object (or function returning an object), containing properties and functions that will be exposed for the entire view
     * hierarchy. Providing a simple communication channel between the parent view and it's children.
     *
     * @see  Region
     *
     * @author Kim
     * @class  ViewScope
     * @constructor
     */
    const ViewScope = EventDispatcher.extend({}, {

        _topLevelViewScope: null,

        /**
         * Get parent view scope of a target(either a region or view)
         * @param {Region | View} target
         * @returns {*|viewScope|viewScope|viewScope}
         * @private
         */
        _getParentViewScope(target) {
            const topLevelViewScope = ViewScope._topLevelViewScope || new ViewScope();

            if (target instanceof Marionette.Region) {
                const parentView = target.parentView;

                // If the viewScope is a function, run it.
                // Default to the parentView.viewScope, which may be from its parent view
                return parentView ? _.result(parentView, "viewScope", parentView.viewScope) : topLevelViewScope;
            }

            return target.viewScope ? target.viewScope : topLevelViewScope;
        },

        /**
         * Defines the top level view scope.
         *
         * @param {Function|Object} viewScope the top level view scope.
         */
        setTopLevelViewScope(viewScope) {
            ViewScope._topLevelViewScope = viewScope;
        },

        /**
         * Register a viewScope for a view and also set up the viewScope hierarchy.
         * @param {Region|View} target
         * @param {View}        view
         */
        register(target, view) {
            // If there's no parent view, we assume to be at the top level
            const parentViewScope = ViewScope._getParentViewScope(target);

            if (view.viewScope) {
                // Create or re-create the view scope. We have to re-create the scope since properties already created
                // for it may have changed, for example if a property reflects a changed principal permission...
                view.viewScope = this._createScope(view, view.viewScope) || // first time
                    this._createScope(view, view.viewScope._factory) || // re-render
                    view.viewScope; // default
                // We no longer need the 'inherit' property
                delete view.viewScope.inherit;
                // Guarantee scope/context belongs to the right object
                _.functions(view.viewScope).forEach((functionName) => {
                    view.viewScope[functionName] = _.bind(view.viewScope[functionName], view);
                });
            }

            // Store the view scope on the added view
            view.viewScope = _.extend(view.viewScope || new ViewScope(), parentViewScope);
        },

        _createScope(view, scope) {
            let viewScope;

            if (_.isFunction(scope)) {
                viewScope = scope.call(view);
                viewScope._factory = scope;
            } else {
                // Assuming the view scope is a plain object, create a new (cloned) object so we don't
                // alter the prototype.
                viewScope = _.clone(scope);
            }

            return viewScope;
        }

    });

    return ViewScope;
});



// WEBPACK FOOTER //
// ./src/shared/ui/common/ViewScope.js